// Management Review — lean module: period · auto-populated inputs · manual notes · actions · minutes.
// Admin/QM only. Reuses internal_corrective_actions (source='Management review') for action items.
// All inputs are READ-ONLY summaries of existing data; no source workflow is modified.
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { isOtherService } from '../lib/service'
import { useAuth } from '../lib/AuthContext'
import { format, parseISO, differenceInDays } from 'date-fns'
import { toast, failed } from '../lib/toast'

const STATUSES = ['Draft', 'Held', 'Closed']
// [key, title, isAuto]
const SECTIONS = [
  ['audit_performance', 'Results of audits / audit performance', true],
  ['internal_audit', 'Internal audit results', true],
  ['complaints', 'Complaints', true],
  ['client_feedback', 'Client feedback (Jotform / external)', false],
  ['corrective_actions', 'Corrective actions', true],
  ['competence', 'Personnel competence and resources', true],
  ['objectives', 'Fulfilment of management system objectives', false],
  ['impartiality', 'Impartiality and COI', true],
  ['risk_actions', 'Status of actions to address risks', false],
  ['retention', 'Records retention', true],
  ['changes', 'Changes affecting the management system', false],
  ['improvements', 'Improvement opportunities', false],
  ['previous_actions', 'Previous action follow-up', true],
]
function fmt(d) { return d ? format(parseISO(d), 'd MMM yyyy') : '—' }
function nextRef(rows) {
  const yr = new Date().getFullYear()
  let max = 0
  for (const r of rows || []) { const m = new RegExp(`^MR-${yr}-(\\d+)$`).exec(r.ref || ''); if (m) max = Math.max(max, +m[1]) }
  return `MR-${yr}-${String(max + 1).padStart(2, '0')}`
}
function humanize(k) { return k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }

// ── Management System Objectives — live status computed from AuditPM data (read-only). ──────────────────
// Mirrors the THREE objectives in the M&P Manual MP-01 Rev 1 (Front Matter). No source data is modified.
// (The former "timely / reports within N days" objective was removed in MP-01 Rev 1 — report timing depends on
//  the client clearing outstanding items, so absolute calendar timeliness is not a fair AO objective.)
function ObjectivesPanel() {
  const [d, setD] = useState(null)
  useEffect(() => { (async () => {
    const today = new Date()
    const yearAgo = new Date(); yearAgo.setFullYear(today.getFullYear() - 1)
    const [aud, ia, mr, jobs] = await Promise.all([
      supabase.from('auditors').select('id,active,is_auditor,approval_status,wsho_status,wsho_licence_expiry,wsh_auditor_expiry,next_competence_review_due').then(r => r.data || []),
      supabase.from('internal_audits').select('id,conducted_date,planned_date,created_at').order('created_at', { ascending: false }).limit(1).then(r => r.data || []),
      supabase.from('management_reviews').select('id,review_date,created_at').order('created_at', { ascending: false }).limit(1).then(r => r.data || []),
      // SAC objectives count bizSAFE / SHMS audits only — never client Internal Audit or OTE jobs.
      supabase.from('jobs').select('id,audit_type,audit_date,report_submitted_at').then(r => (r.data || []).filter(j => !isOtherService(j.audit_type))),
    ])
    const past = x => x && parseISO(x) < today
    const activeAuditors = aud.filter(a => a.active && a.is_auditor)
    const current = activeAuditors.filter(a => a.approval_status === 'Approved'
      && !(a.wsho_status && past(a.wsho_licence_expiry)) && !past(a.wsh_auditor_expiry) && !past(a.next_competence_review_due))
    const recentJobs = jobs.filter(j => j.audit_date && parseISO(j.audit_date) >= yearAgo && j.report_submitted_at)
    const onTime = recentJobs.filter(j => differenceInDays(parseISO(j.report_submitted_at), parseISO(j.audit_date)) <= 14)
    const iaLast = ia[0]?.conducted_date || ia[0]?.planned_date || ia[0]?.created_at
    const mrLast = mr[0]?.review_date || mr[0]?.created_at
    const within12 = x => x && differenceInDays(today, parseISO(x)) <= 366
    setD({ nA: activeAuditors.length, nC: current.length, nJ: recentJobs.length, nT: onTime.length, iaLast, mrLast, iaOk: within12(iaLast), mrOk: within12(mrLast) })
  })() }, [])

  const DOT = { ok: '#639922', warn: '#C77700', none: '#B0B0B0' }
  const BG = { ok: '#EAF3DE', warn: '#FFF3E0', none: 'var(--bg2)' }
  const Row = ({ state, title, target, detail }) => (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderTop: '1px solid var(--border)' }}>
      <span style={{ width: 10, height: 10, borderRadius: 10, background: DOT[state], marginTop: 4, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--text3)', margin: '1px 0 3px' }}>Target: {target}</div>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{detail}</div>
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: BG[state], color: DOT[state] === '#B0B0B0' ? 'var(--text3)' : DOT[state] }}>
        {state === 'ok' ? 'On track' : state === 'warn' ? 'Review' : 'No data'}
      </span>
    </div>
  )

  return (
    <div className="card" style={{ padding: 0, marginBottom: 16 }}>
      <div style={{ padding: '12px 14px 10px' }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Management System Objectives</div>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>Live status from AuditPM — reviewed at each Management Review (M&P Front Matter).</div>
      </div>
      {!d ? <div style={{ padding: '12px 14px', color: 'var(--text3)', fontSize: 13, borderTop: '1px solid var(--border)' }}>Loading…</div> : <>
        <Row state="ok" title="Impartiality upheld"
          target="Zero substantiated impartiality breaches"
          detail="No substantiated breaches recorded — monitored via the Impartiality Register and per‑engagement COI declarations." />
        <Row state={d.nA > 0 && d.nC === d.nA ? 'ok' : 'warn'} title="Competent audit team"
          target="All active auditors current on CT 17 competence, WSHO validity and CPD"
          detail={`${d.nC} of ${d.nA} active auditors current on approval, WSHO validity and competence review.`} />
        <Row state={d.iaOk && d.mrOk ? 'ok' : 'warn'} title="Client confidence & continual improvement"
          target="Internal audit and management review completed on schedule each year"
          detail={`Internal audit: ${fmt(d.iaLast ? d.iaLast.slice(0, 10) : null)} · Management review: ${fmt(d.mrLast ? d.mrLast.slice(0, 10) : null)}.`} />
      </>}
    </div>
  )
}

export default function ManagementReview() {
  const { isAuditor } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId] = useState(null)

  useEffect(() => { if (!isAuditor) load() }, [isAuditor])
  async function load() {
    setLoading(true)
    const { data, error } = await supabase.from('management_reviews').select('*').order('created_at', { ascending: false })
    if (error) console.warn('management_reviews load error (run MANAGEMENT_REVIEW.sql):', error.message)
    setRows(data || []); setLoading(false)
  }

  if (isAuditor) return (
    <div><div className="page-header"><h1>Management Review</h1></div>
      <div className="notice notice-info" style={{ fontSize: 13 }}>This page is not available for the Auditor role.</div></div>
  )
  if (openId) return <ReviewDetail id={openId} onBack={() => { setOpenId(null); load() }} />

  return (
    <div>
      <div className="page-header">
        <h1>Management Review</h1>
        <CreateButton allRows={rows} onCreated={(id) => { load(); setOpenId(id) }} />
      </div>
      <div className="notice notice-info" style={{ fontSize: 13, marginBottom: 14 }}>
        Set a review period, auto-populate inputs from AuditPM, add manual notes (e.g. client feedback), record decisions/actions, and print the minutes.
      </div>
      <ObjectivesPanel />
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Ref</th><th>Review date</th><th>Period</th><th>Chair</th><th>Status</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={5} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={5} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No management reviews yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setOpenId(r.id)}>
                  <td style={{ fontWeight: 500 }}>{r.ref || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.review_date)}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.period_start)} – {fmt(r.period_end)}</td>
                  <td style={{ fontSize: 13 }}>{r.chair || '—'}</td>
                  <td><span className={`badge ${r.status === 'Closed' || r.status === 'Held' ? 'badge-ok' : 'badge-warn'}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function CreateButton({ allRows, onCreated }) {
  const { profile, canApprove, logAction } = useAuth()
  const [busy, setBusy] = useState(false)
  if (!canApprove) return null
  async function create() {
    setBusy(true)
    const ref = nextRef(allRows)
    const today = new Date().toISOString().slice(0, 10)
    const { data, error } = await supabase.from('management_reviews')
      .insert({ ref, review_date: today, status: 'Draft', created_by: profile?.name || 'Staff' }).select().single()
    setBusy(false)
    if (error) { failed("Couldn't create the review", error.message); return }
    if (logAction) logAction('Management review created', 'management_reviews', data.id, ref, {})
    onCreated(data.id)
  }
  return <button className="primary" disabled={busy} onClick={create}>+ New management review</button>
}

// ───────────────────────── Auto-summary builder (read-only counts for the period) ─────────────────────────
async function buildSummaries(start, end) {
  const endTs = end ? end + 'T23:59:59' : null
  const inP = (q, col, isTs) => { // apply period filter if dates given
    if (start) q = q.gte(col, start)
    if (end) q = q.lte(col, isTs ? endTs : end)
    return q
  }
  const [jobs, snaps, ias, iafs, comps, cas, esc, auds, coi, impreg, comps_events, retn] = await Promise.all([
    // bizSAFE / SHMS audits only — client Internal Audit and OTE jobs are not SAC certification audits.
    inP(supabase.from('jobs').select('id,audit_type,status,workflow_state,audit_date,report_submitted,wshc_outcome'), 'audit_date').then(r => (r.data || []).filter(j => !isOtherService(j.audit_type))),
    inP(supabase.from('audit_report_snapshots').select('job_id,version,submitted_at'), 'submitted_at', true).then(r => r.data || []),
    inP(supabase.from('internal_audits').select('id,status,created_at'), 'created_at', true).then(r => r.data || []),
    // Findings are scoped to WHICH internal audits fall in the period (below), not by the finding row's
    // own created_at — a finding is often typed into the system well after the audit was conducted (during
    // write-up, or backfilled), so filtering findings by their own timestamp silently dropped real findings
    // that belonged to an in-period audit. Fetch all findings here; the period cut happens via internal_audit_id.
    supabase.from('internal_audit_findings').select('id,internal_audit_id,finding_type,status,created_at').then(r => r.data || []),
    inP(supabase.from('complaints').select('id,record_type,status,complaint_date'), 'complaint_date').then(r => r.data || []),
    supabase.from('internal_corrective_actions').select('id,status,source,created_at').then(r => r.data || []),
    supabase.from('qm_escalations').select('id,status').then(r => r.data || []),
    supabase.from('auditors').select('id,active,is_decision_maker,dm_authorised,wsho_licence_expiry,wsh_auditor_expiry,next_competence_review_due').then(r => r.data || []),
    inP(supabase.from('auditor_coi_responses').select('id,flagged,submitted_at'), 'submitted_at', true).then(r => r.data || []),
    supabase.from('impartiality_register').select('id').then(r => r.data || []),
    inP(supabase.from('competency_events').select('id,type,event_date'), 'event_date').then(r => r.data || []),
    supabase.from('retention_overview').select('base_status').lte('days_until', 90).then(r => r.data || []),
  ])
  const today = new Date(); const in90 = new Date(Date.now() + 90 * 86400000)
  const exp = (d) => d && new Date(d) <= in90
  const open = (s) => s && s !== 'Closed'
  // Findings belonging to an internal audit that falls in this period — see the fetch note above.
  const iaIds = new Set(ias.map(a => a.id))
  const iafsInPeriod = iafs.filter(f => iaIds.has(f.internal_audit_id))
  const held = jobs.filter(j => j.status !== 'Cancelled')   // a cancelled audit never took place
  return {
    audit_performance: {
      jobs_in_period: held.length,
      completed: held.filter(j => j.status === 'Completed' || j.workflow_state === 'Completed').length,
      outstanding: held.filter(j => (j.workflow_state && j.workflow_state !== 'Completed')).length,
      report_revisions: snaps.filter(s => (s.version || 0) > 1).length,
    },
    internal_audit: {
      internal_audits_in_period: ias.length,
      findings: iafsInPeriod.length,
      major_nc: iafsInPeriod.filter(f => f.finding_type === 'Major NC').length,
      minor_nc: iafsInPeriod.filter(f => f.finding_type === 'Minor NC').length,
      observations: iafsInPeriod.filter(f => f.finding_type === 'Observation').length,
      ofi: iafsInPeriod.filter(f => f.finding_type === 'OFI').length,
      open_findings: iafsInPeriod.filter(f => f.status !== 'Closed').length,
    },
    complaints: {
      total: comps.length,
      open: comps.filter(c => open(c.status)).length,
      closed: comps.filter(c => c.status === 'Closed').length,
    },
    corrective_actions: {
      open: cas.filter(c => open(c.status)).length,
      closed: cas.filter(c => c.status === 'Closed').length,
      raised_in_period: cas.filter(c => c.created_at && (!start || c.created_at >= start) && (!end || c.created_at <= endTs)).length,
      qm_escalations_open: esc.filter(e => open(e.status)).length,
    },
    competence: {
      active_personnel: auds.filter(a => a.active).length,
      decision_makers_authorised: auds.filter(a => a.is_decision_maker && a.dm_authorised).length,
      wsho_expiring_90: auds.filter(a => exp(a.wsho_licence_expiry)).length,
      wsh_expiring_90: auds.filter(a => exp(a.wsh_auditor_expiry)).length,
      competence_review_due: auds.filter(a => a.next_competence_review_due && new Date(a.next_competence_review_due) <= today).length,
      competency_events_in_period: comps_events.length,
    },
    impartiality: {
      coi_declarations_in_period: coi.length,
      coi_flagged: coi.filter(c => c.flagged).length,
      impartiality_register_entries: impreg.length,
    },
    retention: {
      eligible_now: retn.filter(r => r.base_status === 'Eligible for Review').length,
      due_within_90: retn.filter(r => r.base_status === 'Due within 90 days').length,
    },
    previous_actions: {
      open_action_items: cas.filter(c => open(c.status)).length,
    },
  }
}

// ───────────────────────────── Detail ─────────────────────────────
function ReviewDetail({ id, onBack }) {
  const { profile, canApprove, logAction } = useAuth()
  const [r, setR] = useState(null)
  const [inputs, setInputs] = useState([])
  const [actions, setActions] = useState([])
  const [linkable, setLinkable] = useState([])
  const [saving, setSaving] = useState(false)
  const [populating, setPopulating] = useState(false)
  const [caForm, setCaForm] = useState(null)

  useEffect(() => { load() }, [id])
  async function load() {
    const [{ data: mr }, { data: ins }, { data: act }, { data: link }] = await Promise.all([
      supabase.from('management_reviews').select('*').eq('id', id).single(),
      supabase.from('management_review_inputs').select('*').eq('management_review_id', id),
      supabase.from('internal_corrective_actions').select('*').eq('management_review_id', id).order('created_at', { ascending: false }),
      supabase.from('internal_corrective_actions').select('id,issue,status,source').is('management_review_id', null).order('created_at', { ascending: false }),
    ])
    setR(mr || null); setInputs(ins || []); setActions(act || []); setLinkable(link || [])
  }
  const set = (k, v) => setR(p => ({ ...p, [k]: v }))
  const inputFor = (key) => inputs.find(i => i.input_key === key)

  async function saveDetails() {
    setSaving(true)
    const prev = (await supabase.from('management_reviews').select('status').eq('id', id).single()).data?.status
    const payload = {
      ref: r.ref, review_date: r.review_date || null, period_start: r.period_start || null, period_end: r.period_end || null,
      chair: r.chair, attendees: r.attendees, status: r.status || 'Draft', summary: r.summary, updated_at: new Date().toISOString(),
    }
    const { error } = await supabase.from('management_reviews').update(payload).eq('id', id)
    setSaving(false)
    if (error) { failed("Couldn't save", error.message); return }
    if (logAction) {
      logAction('Management review updated', 'management_reviews', id, r.ref, {})
      if (prev !== payload.status && (payload.status === 'Held' || payload.status === 'Closed'))
        logAction(`Management review ${payload.status.toLowerCase()}`, 'management_reviews', id, r.ref, { status: payload.status })
    }
    load()
  }

  async function populate() {
    if (!r.period_start || !r.period_end) { toast('Set the review period first', 'warning', { detail: 'Enter the start and end, save, then populate' }); return }
    setPopulating(true)
    const summaries = await buildSummaries(r.period_start, r.period_end)
    const existing = Object.fromEntries((inputs || []).map(i => [i.input_key, i]))
    const payload = SECTIONS.map(([key, title, isAuto]) => ({
      management_review_id: id, input_key: key, input_title: title,
      auto_summary: isAuto ? (summaries[key] || null) : null,
      manual_notes: existing[key]?.manual_notes ?? null, updated_at: new Date().toISOString(),
    }))
    const { error } = await supabase.from('management_review_inputs').upsert(payload, { onConflict: 'management_review_id,input_key' })
    setPopulating(false)
    if (error) { failed("Couldn't populate", error.message); return }
    if (logAction) logAction('Management review inputs refreshed', 'management_reviews', id, r.ref, {})
    load()
  }
  async function saveNote(key, title, notes) {
    const ex = inputFor(key)
    await supabase.from('management_review_inputs').upsert(
      { management_review_id: id, input_key: key, input_title: title, auto_summary: ex?.auto_summary ?? null, manual_notes: notes, updated_at: new Date().toISOString() },
      { onConflict: 'management_review_id,input_key' })
    load()
  }
  async function linkAction(caId) {
    if (!caId) return
    await supabase.from('internal_corrective_actions').update({ management_review_id: id }).eq('id', caId)
    if (logAction) logAction('Management review corrective action linked', 'management_reviews', id, r.ref, { ca: caId })
    load()
  }
  async function unlinkAction(ca) {
    await supabase.from('internal_corrective_actions').update({ management_review_id: null }).eq('id', ca.id); load()
  }
  async function deleteReview() {
    if (!window.confirm(`Delete management review "${r.ref}"?\n\nThis permanently removes the review and its inputs. Linked corrective actions are kept in the register (just unlinked). This cannot be undone.`)) return
    // Keep corrective actions in the firm's register — unlink them rather than delete.
    await supabase.from('internal_corrective_actions').update({ management_review_id: null }).eq('management_review_id', id)
    await supabase.from('management_review_inputs').delete().eq('management_review_id', id)
    const { error } = await supabase.from('management_reviews').delete().eq('id', id)
    if (error) { failed("Couldn't delete", error.message); return }
    if (logAction) logAction('Management review deleted', 'management_reviews', id, r.ref, {})
    onBack && onBack()
  }

  if (!r) return <div style={{ padding: 20, color: 'var(--text3)' }}>Loading…</div>
  const ro = !canApprove

  return (
    <div>
      <div className="page-header">
        <h1>{r.ref} — Management Review</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => printMinutes(r, inputs, actions)}>Print minutes</button>
          {!ro && <button className="danger" onClick={deleteReview}>Delete</button>}
          <button onClick={onBack}>← Back</button>
        </div>
      </div>

      {/* Details */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h2 className="card-title">Review details</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div className="field"><label>Reference</label><input value={r.ref || ''} disabled={ro} onChange={e => set('ref', e.target.value)} /></div>
          <div className="field"><label>Status</label><select value={r.status || 'Draft'} disabled={ro} onChange={e => set('status', e.target.value)}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select></div>
          <div className="field"><label>Review date</label><input type="date" value={r.review_date || ''} disabled={ro} onChange={e => set('review_date', e.target.value)} /></div>
          <div className="field"><label>Chair</label><input value={r.chair || ''} disabled={ro} onChange={e => set('chair', e.target.value)} /></div>
          <div className="field"><label>Period start</label><input type="date" value={r.period_start || ''} disabled={ro} onChange={e => set('period_start', e.target.value)} /></div>
          <div className="field"><label>Period end</label><input type="date" value={r.period_end || ''} disabled={ro} onChange={e => set('period_end', e.target.value)} /></div>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Attendees</label><input value={r.attendees || ''} disabled={ro} onChange={e => set('attendees', e.target.value)} placeholder="names / roles" /></div>
        </div>
        {!ro && <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button className="primary" onClick={saveDetails} disabled={saving}>{saving ? 'Saving…' : 'Save details'}</button>
          <button onClick={populate} disabled={populating}>{populating ? 'Populating…' : 'Populate / refresh inputs'}</button>
        </div>}
      </div>

      {/* Inputs */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h2 className="card-title">Review inputs</h2>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>Auto figures are a snapshot for the review period. Add manual notes (e.g. Jotform client feedback, changes, improvements) in each section.</div>
        {SECTIONS.map(([key, title, isAuto]) => {
          const inp = inputFor(key)
          const sum = inp?.auto_summary
          return (
            <div key={key} style={{ borderTop: '1px solid var(--border)', padding: '10px 0' }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{title}{!isAuto && <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 13 }}> · manual</span>}</div>
              {isAuto && (sum
                ? <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: '2px 16px' }}>
                    {Object.entries(sum).map(([k, v]) => <span key={k}><b style={{ color: 'var(--text)' }}>{v}</b> {humanize(k)}</span>)}
                  </div>
                : <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 6, fontStyle: 'italic' }}>Not populated yet — click “Populate / refresh inputs”.</div>)}
              {ro
                ? <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{inp?.manual_notes || '—'}</div>
                : <textarea rows={2} defaultValue={inp?.manual_notes || ''} placeholder="Notes / discussion for this input…"
                    onBlur={e => saveNote(key, title, e.target.value)} style={{ width: '100%' }} />}
            </div>
          )
        })}
      </div>

      {/* Actions (reuse internal_corrective_actions) */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h2 className="card-title">Decisions &amp; action items ({actions.length})</h2>
          {!ro && <button className="sm primary" onClick={() => setCaForm({ status: 'Open' })}>+ New action</button>}
        </div>
        {!ro && linkable.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, fontSize: 13 }}>
            <span style={{ color: 'var(--text3)' }}>Link existing corrective action:</span>
            <select defaultValue="" onChange={e => { linkAction(e.target.value); e.target.value = '' }}>
              <option value="">— choose —</option>
              {linkable.map(c => <option key={c.id} value={c.id}>{(c.issue || 'CA').slice(0, 40)} ({c.status})</option>)}
            </select>
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead><tr><th>Issue</th><th>Action required</th><th>Owner</th><th>Due</th><th>Status</th>{!ro && <th></th>}</tr></thead>
            <tbody>
              {actions.length === 0 && <tr><td colSpan={ro ? 5 : 6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 18 }}>No action items.</td></tr>}
              {actions.map(c => (
                <tr key={c.id}>
                  <td style={{ fontSize: 13, maxWidth: 240 }}>{c.issue || '—'}</td>
                  <td style={{ fontSize: 13, maxWidth: 200, color: 'var(--text2)' }}>{c.corrective_action_plan || c.action_required || '—'}</td>
                  <td style={{ fontSize: 13 }}>{c.assigned_name || c.owner || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(c.due_date)}</td>
                  <td><span className={`badge ${c.status === 'Closed' ? 'badge-ok' : 'badge-warn'}`}>{c.status || 'Open'}</span></td>
                  {!ro && <td><button className="sm" onClick={() => unlinkAction(c)}>Unlink</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 6 }}>Action items are the firm’s corrective-action register (source “Management review”) — also visible under Complaints → Internal Actions.</div>
      </div>

      {/* Minutes */}
      <div className="card">
        <h2 className="card-title">Discussion / decisions / minutes</h2>
        {ro ? <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{r.summary || '—'}</div>
          : <><textarea rows={5} value={r.summary || ''} onChange={e => set('summary', e.target.value)} style={{ width: '100%' }} />
            <div style={{ marginTop: 10 }}><button className="primary" onClick={saveDetails} disabled={saving}>{saving ? 'Saving…' : 'Save minutes'}</button></div></>}
      </div>

      {caForm && <ActionForm reviewId={id} reviewRef={r.ref} profile={profile} logAction={logAction} onClose={() => setCaForm(null)} onSaved={() => { setCaForm(null); load() }} />}
    </div>
  )
}

function ActionForm({ reviewId, reviewRef, profile, logAction, onClose, onSaved }) {
  const [f, setF] = useState({ status: 'Open' })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF(p => ({ ...p, [k]: v }))
  async function save() {
    if (!(f.issue || '').trim()) { toast('Enter the issue or decision', 'warning'); return }
    setBusy(true)
    const { error } = await supabase.from('internal_corrective_actions').insert({
      source: 'Management review', management_review_id: reviewId, issue: f.issue, action_required: f.action_required || null,
      owner: f.owner || null, due_date: f.due_date || null, status: f.status || 'Open', created_by: profile?.name || 'Staff',
    })
    setBusy(false)
    if (error) { failed("Couldn't save", error.message); return }
    if (logAction) logAction('Management review corrective action created', 'management_reviews', reviewId, reviewRef, {})
    onSaved()
  }
  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 60 }}>
      <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 520, width: '100%' }}>
        <h2 style={{ marginBottom: 14 }}>New action item</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Issue / decision *</label><textarea rows={2} value={f.issue || ''} onChange={e => set('issue', e.target.value)} /></div>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Action required</label><textarea rows={2} value={f.action_required || ''} onChange={e => set('action_required', e.target.value)} /></div>
          <div className="field"><label>Owner</label><input value={f.owner || ''} onChange={e => set('owner', e.target.value)} /></div>
          <div className="field"><label>Due date</label><input type="date" value={f.due_date || ''} onChange={e => set('due_date', e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save action'}</button>
          <button onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ───────────────────────────── Printable minutes ─────────────────────────────
function printMinutes(r, inputs, actions) {
  const esc = s => (s == null ? '' : String(s)).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
  const byKey = Object.fromEntries((inputs || []).map(i => [i.input_key, i]))
  const inputBlocks = SECTIONS.map(([key, title]) => {
    const inp = byKey[key]
    const sum = inp?.auto_summary
    const auto = sum ? '<div style="font-size:11px;color:#555">' + Object.entries(sum).map(([k, v]) => `${esc(v)} ${esc(k.replace(/_/g, ' '))}`).join(' · ') + '</div>' : ''
    const notes = inp?.manual_notes ? `<div style="white-space:pre-wrap">${esc(inp.manual_notes)}</div>` : '<div style="color:#999">—</div>'
    return `<tr><td style="width:230px;vertical-align:top"><b>${esc(title)}</b></td><td>${auto}${notes}</td></tr>`
  }).join('')
  const actRows = (actions || []).map(c => `<tr><td>${esc(c.issue)}</td><td>${esc(c.corrective_action_plan || c.action_required)}</td><td>${esc(c.assigned_name || c.owner)}</td><td>${esc(c.due_date)}</td><td>${esc(c.status)}</td></tr>`).join('')
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(r.ref)} Management Review Minutes</title>
    <style>body{font-family:Arial,Helvetica,sans-serif;color:#222;margin:28px;font-size:12.5px}
    h1{font-size:18px;margin:0 0 2px}h2{font-size:13px;border-bottom:1px solid #999;padding-bottom:3px;margin:18px 0 8px}
    table{width:100%;border-collapse:collapse;margin-top:4px}th,td{border:0.5px solid #bbb;padding:5px 7px;text-align:left;vertical-align:top;font-size:11.5px}
    th{background:#eee}.kv{margin:2px 0}.kv b{display:inline-block;width:140px;color:#555}</style></head><body>
    <h1>Management Review Minutes</h1><div style="color:#666">${esc(r.ref)}</div>
    <h2>Details</h2>
    <div class="kv"><b>Review date</b> ${esc(r.review_date)}</div>
    <div class="kv"><b>Period covered</b> ${esc(r.period_start)} to ${esc(r.period_end)}</div>
    <div class="kv"><b>Chair</b> ${esc(r.chair)}</div>
    <div class="kv"><b>Attendees</b> ${esc(r.attendees)}</div>
    <div class="kv"><b>Status</b> ${esc(r.status)}</div>
    <h2>Review inputs</h2><table><tbody>${inputBlocks}</tbody></table>
    <h2>Decisions &amp; action items (${(actions || []).length})</h2>
    <table><thead><tr><th>Issue / decision</th><th>Action required</th><th>Owner</th><th>Due</th><th>Status</th></tr></thead>
    <tbody>${actRows || '<tr><td colspan="5">No action items.</td></tr>'}</tbody></table>
    <h2>Discussion / decisions / minutes</h2><div style="white-space:pre-wrap">${esc(r.summary) || '—'}</div>
    </body></html>`
  const w = window.open('', '_blank')
  if (!w) { toast('Allow pop-ups to print the minutes', 'warning'); return }
  w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300)
}
